/**
 * Agrupa a serie diaria da Central em dia ou semana.
 *
 * Decisoes nao obvias:
 *  1. Ate 14 dias de janela, uma barra por dia; acima, uma por semana
 *     (segunda a domingo). "Desde o inicio" (janela nula) cai em semana.
 *     Nao ha seletor: a granularidade segue a janela do cabecalho.
 *  2. Toda conta de data e em UTC sobre `YYYY-MM-DD`: o dia ja veio fechado
 *     pelo servidor, e `new Date('2026-09-24')` num fuso com horario de verao
 *     poderia cair no dia anterior.
 *  3. Dinheiro e somado em centavos inteiros; nada e dividido aqui. A margem
 *     do balde e comissao menos custo, tambem inteira.
 *  4. Custo `null` (nao medido pelo motor real) NAO vira zero: o balde fica
 *     com custo `null` se QUALQUER dia dele vier sem custo, e a margem dele
 *     tambem. Somar null como 0 mostraria margem igual a comissao.
 */

import type { PlatformDailyPoint } from './types';

export type Granularidade = 'dia' | 'semana';

export const LIMITE_DIAS_POR_DIA = 14;
const DIA_MS = 86_400_000;

export interface Balde {
  /** Chave ordenavel: o proprio dia, ou a segunda-feira da semana, em YYYY-MM-DD. */
  readonly chave: string;
  /** Rotulo curto do eixo X: "24/09" ou "21/09–27/09". */
  readonly rotulo: string;
  readonly abandonadoCount: number;
  readonly recuperadoCount: number;
  readonly recuperadoCents: number;
  readonly comissaoCents: number;
  /** `null` = custo nao medido (types.ts, decisao 7). */
  readonly custoCents: number | null;
}

export interface SerieAgrupada {
  readonly granularidade: Granularidade;
  readonly baldes: readonly Balde[];
  readonly labels: readonly string[];
  readonly recuperadoCents: readonly number[];
  readonly comissaoCents: readonly number[];
  readonly custoCents: readonly (number | null)[];
  /** `comissao - custo` por balde; pode ser negativo; `null` sem custo medido. */
  readonly margemCents: readonly (number | null)[];
}

/** `YYYY-MM-DD` -> meia-noite UTC. Entrada invalida vira a epoca, nunca NaN. */
function dataUtc(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

function somarDias(date: Date, dias: number): Date {
  return new Date(date.getTime() + dias * DIA_MS);
}

/** Segunda-feira da semana do dia (0 = segunda ... 6 = domingo). */
function segundaDaSemana(date: Date): Date {
  return somarDias(date, -((date.getUTCDay() + 6) % 7));
}

function yyyymmdd(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function ddmm(date: Date): string {
  const d = String(date.getUTCDate()).padStart(2, '0');
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${d}/${m}`;
}

export function granularidadeDaJanela(dias: number | null): Granularidade {
  return dias !== null && dias <= LIMITE_DIAS_POR_DIA ? 'dia' : 'semana';
}

export function chaveDoBalde(day: string, granularidade: Granularidade): string {
  return granularidade === 'dia' ? day.slice(0, 10) : yyyymmdd(segundaDaSemana(dataUtc(day)));
}

export function rotuloDoBalde(chave: string, granularidade: Granularidade): string {
  const inicio = dataUtc(chave);
  return granularidade === 'dia' ? ddmm(inicio) : `${ddmm(inicio)}–${ddmm(somarDias(inicio, 6))}`;
}

export function agruparSerie(points: readonly PlatformDailyPoint[], granularidade: Granularidade): SerieAgrupada {
  const baldes = new Map<string, Balde>();
  for (const p of points) {
    const chave = chaveDoBalde(p.day, granularidade);
    const atual = baldes.get(chave) ?? {
      chave,
      rotulo: rotuloDoBalde(chave, granularidade),
      abandonadoCount: 0,
      recuperadoCount: 0,
      recuperadoCents: 0,
      comissaoCents: 0,
      custoCents: 0,
    };
    baldes.set(chave, {
      ...atual,
      abandonadoCount: atual.abandonadoCount + p.abandonedCount,
      recuperadoCount: atual.recuperadoCount + p.recoveredCount,
      recuperadoCents: atual.recuperadoCents + p.recoveredAmountCents,
      comissaoCents: atual.comissaoCents + p.commissionCents,
      custoCents: atual.custoCents === null || p.costCents === null ? null : atual.custoCents + p.costCents,
    });
  }
  // Chaves YYYY-MM-DD ordenam como texto; a ordem cronologica nao depende de a
  // serie ter chegado ordenada.
  const lista = [...baldes.values()].sort((a, b) => (a.chave < b.chave ? -1 : a.chave > b.chave ? 1 : 0));
  return {
    granularidade,
    baldes: lista,
    labels: lista.map((b) => b.rotulo),
    recuperadoCents: lista.map((b) => b.recuperadoCents),
    comissaoCents: lista.map((b) => b.comissaoCents),
    custoCents: lista.map((b) => b.custoCents),
    margemCents: lista.map((b) => (b.custoCents === null ? null : b.comissaoCents - b.custoCents)),
  };
}
